<script>
export default {
  inheritAttrs: false,
  props: {
    mask: {
      type: [String, Function],
      default: '',
    },
    phone: {
      type: [String, null],
      default: null,
    },
    phoneExt: {
      type: [String, null],
      default: null,
    },
    phoneLabel: {
      type: String,
      default: 'Work Line',
    },
    phonePlaceholder: {
      type: String,
      default: 'xxx-xxx-xxxx',
    },
    phoneExtLabel: {
      type: String,
      default: 'Ext.',
    },
    rules: {
      type: String,
      default: '',
    },
    editMode: {
      type: Boolean,
    },
    hideExt: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    error: {
      type: String,
      default: '',
    },
    options: {
      type: Object,
      default: () => ({
        hideAsterisk: false,
      }),
    },
  },
  data() {
    return {
      modelPhone: this.phone,
      modelPhoneExt: this.phoneExt,
    }
  },
  computed: {
    formattedValue() {
      if (!this.modelPhone) {
        return 'N/A'
      }

      if (!this.modelPhoneExt) {
        return this.modelPhone
      }

      return `${this.modelPhone} Ext. ${this.modelPhoneExt}`
    },
    isRequired() {
      return this.rules.includes('required')
    },
  },

  watch: {
    phone: {
      handler: function (newVal) {
        this.modelPhone = newVal
      },
    },
    phoneExt: {
      handler: function (newVal) {
        this.modelPhoneExt = newVal
      },
    },
  },
  methods: {
    onInput(inputEvent, name) {
      let value = inputEvent
      if (inputEvent instanceof Event) {
        value = inputEvent.target?.value || null
      }
      if (name === 'phone') {
        this.$emit('update:phone', value)
      }

      if (name === 'phoneExt') {
        this.$emit('update:phoneExt', value)
      }
    },
  },
}
</script>

<template>
  <div :class="[$style.phoneFieldsContainer, editMode ? $style.phoneFieldsContainerEditing : '']">
    <div class="flex-grow-1">
      <div :class="$style.container">
        <div :class="$style.labelContainer">
          <div>
            <p :class="$style.label" class="p1">
              {{ phoneLabel }}:
              <span
                v-if="isRequired && editMode && !options.hideAsterisk"
                data-required-asterisk
                :class="$style.requiredAsterisk"
                >*</span
              >
            </p>
          </div>
        </div>
        <template v-if="!editMode">
          <div :class="$style.valueContainer">
            <h4 v-if="modelPhone" :class="$style.value">{{ formattedValue || 'N/A' }}</h4>
            <h4 v-else :class="$style.value">{{ 'N/A' }}</h4>
            <slot name="afterValue"></slot>
          </div>
        </template>
        <template v-if="editMode">
          <div>
            <ValidationProvider
              v-slot="{ errors, changed }"
              class="d-contents"
              immediate
              :debounce="300"
              :name="phoneLabel"
              :rules="modelPhoneExt ? 'required|validPhoneNumber' : rules">
              <BaseInputText
                v-model="modelPhone"
                :class="$style.phoneField"
                :mask="'###-###-####'"
                :name="phoneLabel"
                :disabled="disabled"
                :error="errors[0]"
                :placeholder="phonePlaceholder"
                :changed="changed"
                @input="onInput($event, 'phone')"
                @blur="$emit('blur')" />
            </ValidationProvider>
          </div>
        </template>
      </div>
    </div>
    <div v-if="!hideExt && editMode">
      <div :class="$style.container">
        <div :class="$style.labelContainer">
          <div>
            <p :class="$style.label" class="p1">{{ phoneExtLabel }}:</p>
          </div>
        </div>
        <template v-if="editMode">
          <div>
            <ValidationProvider v-slot="{ changed }" class="d-contents" :debounce="300" :name="phoneExtLabel">
              <BaseInputText
                v-model="modelPhoneExt"
                :class="$style.extField"
                :name="phoneExtLabel"
                :disabled="disabled"
                :placeholder="'Ext.'"
                :mask="'######'"
                :changed="changed"
                @input="onInput($event, 'phoneExt')"></BaseInputText>
            </ValidationProvider>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  overflow-y: initial;

  div[class*='errorContainer'] {
    position: relative;
  }
}

.valueContainer {
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  word-break: break-all;

  h4 {
    margin: 0;
  }
}

.labelContainer {
  position: relative;
  display: flex;
  align-items: center;

  div[data-tooltip-icon] {
    position: initial;
    display: flex;
    align-items: center;
    margin-left: 8px;
  }
}

.label {
  margin-top: 10px;
  margin-bottom: 10px;
  color: $dark-gray;
}

.value {
  font-weight: 700;
  color: $primary-blue;
  overflow-wrap: break-word;
}

.phoneFieldsContainer {
  display: flex;
  min-width: min(100%, 200px);

  .phoneField {
    margin-right: 10px;
  }

  .extField {
    width: 90px;
  }
}

.phoneFieldsContainerEditing {
  min-width: min(100%, 270px);
}

.requiredAsterisk {
  font-weight: 700;
  color: $secondary-red;
}

.errorMessage {
  @extend %error-message;

  text-align: right;
}
</style>
